// Mirror of backend `models/src/bi/format.ts`, verbatim: one formatter for
// every surface, the same text the AI is handed.

import {
  BiCell,
  BiFieldRole,
  biLabelledCell,
  BiQueryResult,
  BiResultColumn,
  BiValueLabels,
} from './result';
import { BiFormat, BiVizSpec, BiVizType } from './viz';

/**
 * How a number reads (spec §6.6). Values stay raw everywhere — a rate is a
 * fraction, money is in cents, a duration in minutes — and this module is
 * the one place that turns them into text, on every surface: the client's
 * charts, tables and KPIs mirror it verbatim, and the AI is handed its
 * output.
 */
export enum BiDisplayFormatKind {
  Number = 'number',
  /** Cents, shown as yuan. */
  Currency = 'currency',
  /** A fraction: 0.5 shows as 50%. */
  Percent = 'percent',
  /** Minutes, shown as hours and minutes. */
  Duration = 'duration',
}

/** What an empty value (null, a division by zero) shows as. */
export enum BiEmptyAs {
  Dash = '—',
  Zero = '0',
}

export type BiDecimals = 0 | 1 | 2;

export const kBiDecimals: readonly BiDecimals[] = [0, 1, 2];

export interface BiDisplayFormat {
  kind: BiDisplayFormatKind;
  /**
   * Places after the point, always shown. Omitted: as many as the value
   * needs, up to the kind's default (2 for a number, 1 for a percent;
   * money always shows 2).
   */
  decimals?: BiDecimals;
  /** 12.5万 / 1.2亿 from ten thousand up. */
  compact?: boolean;
  /** A suffix: 次 / 台 / km. */
  unit?: string;
  /** Omitted: a dash. */
  emptyAs?: BiEmptyAs;
}

/** Where a format is given: a full format, or its kind as a shorthand. */
export type BiFormatSpec = BiFormat | BiDisplayFormat;

export enum BiDisplayFormatOption {
  Decimals = 'decimals',
  Compact = 'compact',
  Unit = 'unit',
  EmptyAs = 'emptyAs',
}

/** The options each kind takes; any other is refused (the editor hides it). */
export const kBiDisplayFormatOptions: Record<
  BiDisplayFormatKind,
  readonly BiDisplayFormatOption[]
> = {
  [BiDisplayFormatKind.Number]: [
    BiDisplayFormatOption.Decimals,
    BiDisplayFormatOption.Compact,
    BiDisplayFormatOption.Unit,
    BiDisplayFormatOption.EmptyAs,
  ],
  [BiDisplayFormatKind.Currency]: [
    BiDisplayFormatOption.Decimals,
    BiDisplayFormatOption.Compact,
    BiDisplayFormatOption.EmptyAs,
  ],
  [BiDisplayFormatKind.Percent]: [
    BiDisplayFormatOption.Decimals,
    BiDisplayFormatOption.EmptyAs,
  ],
  [BiDisplayFormatKind.Duration]: [BiDisplayFormatOption.EmptyAs],
};

export const kBiMaxFormatUnit = 10;

export const kBiDisplayFormatKindNames: Record<BiDisplayFormatKind, string> = {
  [BiDisplayFormatKind.Number]: '数字',
  [BiDisplayFormatKind.Currency]: '金额',
  [BiDisplayFormatKind.Percent]: '百分比',
  [BiDisplayFormatKind.Duration]: '时长',
};

/** A count: a whole number. */
export const kBiCountFormat: BiDisplayFormat = {
  kind: BiDisplayFormatKind.Number,
  decimals: 0,
};

/** A number with nothing known about it: up to 2 decimals. */
export const kBiNumberFormat: BiDisplayFormat = {
  kind: BiDisplayFormatKind.Number,
};

const kShorthands: Record<BiFormat, BiDisplayFormat> = {
  [BiFormat.Integer]: kBiCountFormat,
  [BiFormat.Decimal]: kBiNumberFormat,
  [BiFormat.Currency]: { kind: BiDisplayFormatKind.Currency },
  [BiFormat.Percent]: { kind: BiDisplayFormatKind.Percent },
  [BiFormat.Duration]: { kind: BiDisplayFormatKind.Duration },
};

/** A format as a full format: a shorthand expanded, a full one as given. */
export function biDisplayFormat(spec: BiFormatSpec): BiDisplayFormat {
  return typeof spec === 'string' ? kShorthands[spec] : spec;
}

/**
 * The format a column shows in (spec §6.6): the viz's own override, else
 * the format the result resolved for it (the ratio's, else the measure's
 * default, else the type's). Dimensions show as their values: undefined.
 */
export function biColumnFormat(
  column: BiResultColumn,
  viz?: BiVizSpec,
): BiDisplayFormat | undefined {
  const override = !viz
    ? undefined
    : viz.type === BiVizType.Kpi
    ? viz.value === column.key
      ? viz.format
      : undefined
    : viz.type === BiVizType.KpiGroup
    ? viz.items.find(item => item.value === column.key)?.format
    : viz.formats &&
      Object.prototype.hasOwnProperty.call(viz.formats, column.key)
    ? viz.formats[column.key]
    : undefined;
  return biColumnFormatWith(column, override);
}

/** {@link biColumnFormat} with the viz's override for the column given. */
export function biColumnFormatWith(
  column: BiResultColumn,
  override: BiFormatSpec | undefined,
): BiDisplayFormat | undefined {
  if (column.role !== BiFieldRole.Measure) return undefined;
  if (override !== undefined) return biDisplayFormat(override);
  return column.format !== undefined
    ? biDisplayFormat(column.format)
    : kBiNumberFormat;
}

/**
 * The kinds a column may be shown as, from what its value is: money stays
 * money (it is cents), a rate or a duration may also read as a plain
 * number. A ratio is a division whose meaning its writer states, so it
 * takes any kind.
 */
export function biDisplayFormatKindsFor(
  base: BiDisplayFormat,
  ratio: boolean,
): BiDisplayFormatKind[] {
  if (ratio) return Object.values(BiDisplayFormatKind);
  switch (base.kind) {
    case BiDisplayFormatKind.Currency:
      return [BiDisplayFormatKind.Currency];
    case BiDisplayFormatKind.Percent:
      return [BiDisplayFormatKind.Percent, BiDisplayFormatKind.Number];
    case BiDisplayFormatKind.Duration:
      return [BiDisplayFormatKind.Duration, BiDisplayFormatKind.Number];
    default:
      return [BiDisplayFormatKind.Number];
  }
}

/** A format keeping only the options its kind takes. */
export function biDisplayFormatFitting(
  format: BiDisplayFormat,
): BiDisplayFormat {
  const options = kBiDisplayFormatOptions[format.kind];
  const fitting: BiDisplayFormat = { kind: format.kind };
  if (
    format.decimals !== undefined &&
    options.includes(BiDisplayFormatOption.Decimals)
  )
    fitting.decimals = format.decimals;
  if (format.compact && options.includes(BiDisplayFormatOption.Compact))
    fitting.compact = true;
  if (format.unit && options.includes(BiDisplayFormatOption.Unit))
    fitting.unit = format.unit;
  if (format.emptyAs && options.includes(BiDisplayFormatOption.EmptyAs))
    fitting.emptyAs = format.emptyAs;
  return fitting;
}

/**
 * A format as one token, for summaries: the shorthand, or the kind with its
 * options (`currency:0:compact`, `number:unit=次`).
 */
export function biFormatSpecText(spec: BiFormatSpec): string {
  if (typeof spec === 'string') return spec;
  return [
    spec.kind,
    ...(spec.decimals !== undefined ? [String(spec.decimals)] : []),
    ...(spec.compact ? ['compact'] : []),
    ...(spec.unit ? [`unit=${spec.unit}`] : []),
    ...(spec.emptyAs ? [`empty=${spec.emptyAs}`] : []),
  ].join(':');
}

const kDash = '—';

/** `1234567.5` → `1,234,567.5`; the digits are kept as given. */
function groupDigits(text: string): string {
  const [whole, fraction] = text.split('.');
  let grouped = '';
  for (let end = whole.length; end > 0; end -= 3) {
    const group = whole.slice(Math.max(0, end - 3), end);
    grouped = grouped ? `${group},${grouped}` : group;
  }
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

/**
 * A non-negative value with `places` decimals, grouped; `trim` drops the
 * trailing zeros (as many as the value needs, up to `places`).
 */
function digits(value: number, places: number, trim: boolean): string {
  let text = value.toFixed(places);
  if (trim) text = text.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return groupDigits(text);
}

/** `|value|` in 万 / 亿 when it is that large, else null. */
function compactDigits(
  size: number,
  decimals: BiDecimals | undefined,
): string | null {
  if (size < 1e4) return null;
  const places = decimals ?? 1;
  const trim = decimals === undefined;
  // Rounding may carry 万 over to 亿: 99,999,600 is 1亿, not 10,000万.
  const wan = Number((size / 1e4).toFixed(places));
  return wan < 1e4
    ? `${digits(size / 1e4, places, trim)}万`
    : `${digits(size / 1e8, places, trim)}亿`;
}

function signed(negative: boolean, text: string): string {
  // A value that rounds to zero shows no sign.
  return negative && /[1-9]/.test(text) ? `-${text}` : text;
}

function withUnit(text: string, unit: string | undefined): string {
  if (!unit) return text;
  // A Latin unit reads apart from the number (12 km); a Chinese one joins.
  return /^[A-Za-z]/.test(unit) ? `${text} ${unit}` : `${text}${unit}`;
}

function duration(minutes: number): string {
  const total = Math.round(minutes);
  if (Math.abs(total) < 60) return `${total === 0 ? 0 : total}分钟`;
  const sign = total < 0 ? '-' : '';
  const hours = groupDigits(String(Math.floor(Math.abs(total) / 60)));
  const rest = Math.abs(total) % 60;
  return rest ? `${sign}${hours}小时${rest}分钟` : `${sign}${hours}小时`;
}

function formatNumber(value: number, format: BiDisplayFormat): string {
  const negative = value < 0;
  const size = Math.abs(value);
  switch (format.kind) {
    case BiDisplayFormatKind.Currency: {
      const yuan = size / 100;
      const text =
        (format.compact ? compactDigits(yuan, format.decimals) : null) ??
        digits(yuan, format.decimals ?? 2, false);
      return signed(negative, `¥${text}`);
    }
    case BiDisplayFormatKind.Percent:
      return `${signed(
        negative,
        digits(size * 100, format.decimals ?? 1, format.decimals === undefined),
      )}%`;
    case BiDisplayFormatKind.Duration:
      return duration(value);
    default: {
      const text =
        (format.compact ? compactDigits(size, format.decimals) : null) ??
        digits(
          size,
          format.decimals ?? (Number.isInteger(size) ? 0 : 2),
          format.decimals === undefined,
        );
      return withUnit(signed(negative, text), format.unit);
    }
  }
}

/** Cents as yuan from the digits, so an amount beyond 2^53 stays exact. */
function exactYuan(negative: boolean, cents: string): string {
  const padded = cents.padStart(3, '0');
  const text = `${groupDigits(padded.slice(0, -2))}.${padded.slice(-2)}`;
  return signed(negative, `¥${text}`);
}

/**
 * A value as text in a format (none: a plain number). An integer beyond
 * 2^53 arrives as a string and keeps its digits where the format shows
 * them all; a string that is not a number is shown as it is.
 */
export function formatBiValue(cell: BiCell, spec?: BiFormatSpec): string {
  const format = spec === undefined ? kBiNumberFormat : biDisplayFormat(spec);
  if (cell === null || cell === '' || cell === undefined)
    return format.emptyAs === BiEmptyAs.Zero ? formatNumber(0, format) : kDash;
  if (typeof cell === 'boolean') return cell ? '是' : '否';
  if (typeof cell === 'string') {
    const big = /^(-?)(\d+)$/.exec(cell);
    if (!big) return cell;
    const exact = !format.compact && !Number.isSafeInteger(Number(cell));
    if (exact && format.kind === BiDisplayFormatKind.Number) {
      const places = format.decimals ? `.${'0'.repeat(format.decimals)}` : '';
      const text = `${groupDigits(big[2])}${places}`;
      return withUnit(signed(big[1] === '-', text), format.unit);
    }
    if (
      exact &&
      format.kind === BiDisplayFormatKind.Currency &&
      (format.decimals ?? 2) === 2
    )
      return exactYuan(big[1] === '-', big[2]);
    return formatNumber(Number(cell), format);
  }
  if (!Number.isFinite(cell))
    return format.emptyAs === BiEmptyAs.Zero ? formatNumber(0, format) : kDash;
  return formatNumber(cell, format);
}

/**
 * A cell as text in its column: a measure by its format (from
 * {@link biColumnFormat}); a dimension as its value's label when it is a
 * coded one, else as the value itself, so a year, a phone number or an
 * order number keeps its digits ungrouped.
 */
export function formatBiCell(
  cell: BiCell,
  format: BiDisplayFormat | undefined,
  valueLabels?: BiValueLabels,
): string {
  if (format) return formatBiValue(cell, format);
  if (cell === null || cell === '' || cell === undefined) return kDash;
  if (typeof cell === 'boolean') return cell ? '是' : '否';
  return String(biLabelledCell(cell, valueLabels));
}

/** Every row of a result as text, each column in its format. */
export function formatBiRows(
  result: BiQueryResult,
  viz?: BiVizSpec,
): string[][] {
  const formats = result.columns.map(column => biColumnFormat(column, viz));
  return result.rows.map(row =>
    row.map((cell, index) =>
      formatBiCell(cell, formats[index], result.columns[index].valueLabels),
    ),
  );
}

/**
 * A value in the unit a chart plots it in — yuan for money, percentage
 * points for a percent, as stored otherwise — so an axis reads like the
 * values beside it. Undefined for no value (a gap).
 */
export function biChartValue(
  cell: BiCell,
  spec?: BiFormatSpec,
): number | undefined {
  const value = numeric(cell);
  if (value === null) return undefined;
  const kind = spec === undefined ? undefined : biDisplayFormat(spec).kind;
  const scaled =
    kind === BiDisplayFormatKind.Currency
      ? value / 100
      : kind === BiDisplayFormatKind.Percent
      ? value * 100
      : value;
  return Math.round(scaled * 100) / 100;
}

/** The unit {@link biChartValue} plots in, captioned once on an axis. */
export function biChartUnit(spec?: BiFormatSpec): string | null {
  if (spec === undefined) return null;
  const format = biDisplayFormat(spec);
  switch (format.kind) {
    case BiDisplayFormatKind.Currency:
      return '元';
    case BiDisplayFormatKind.Percent:
      return '%';
    case BiDisplayFormatKind.Duration:
      return '分钟';
    default:
      return format.unit ?? null;
  }
}

/** Up to `places` decimals, trailing zeros dropped, digits ungrouped. */
function plain(value: number, places: number): string {
  const text = value
    .toFixed(places)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
  return text === '-0' ? '0' : text;
}

/**
 * A value-axis tick in chart units, short: `12000` → `1.2万`,
 * `350000000` → `3.5亿`. The axis's unit is captioned once, not repeated
 * on every tick.
 */
export function formatBiAxisTick(value: number): string {
  const size = Math.abs(value);
  if (size >= 1e8) return `${plain(value / 1e8, 2)}亿`;
  if (size >= 1e4) return `${plain(value / 1e4, 2)}万`;
  return plain(value, size < 10 && !Number.isInteger(value) ? 2 : 1);
}

/**
 * A data label in chart units short enough for a narrow bar: one decimal
 * of 万 / 亿 (`148200` → `14.8万`); the unit is captioned once.
 */
export function formatBiShortLabel(value: number): string {
  const size = Math.abs(value);
  if (size >= 1e8) return `${plain(value / 1e8, 1)}亿`;
  if (size >= 1e4) return `${plain(value / 1e4, 1)}万`;
  return plain(value, size < 10 && !Number.isInteger(value) ? 1 : 0);
}

export enum BiDeltaTrend {
  Up = 'up',
  Down = 'down',
  Flat = 'flat',
}

/** How a value moved against the previous period. */
export interface BiDelta {
  trend: BiDeltaTrend;
  /** The size of the move: `12.5%`, or `3.2个百分点` for a percent. */
  text: string;
}

function numeric(cell: BiCell): number | null {
  if (cell === null || typeof cell === 'boolean') return null;
  const value = Number(cell);
  return cell === '' || !Number.isFinite(value) ? null : value;
}

/**
 * How `current` moved from `previous`. A percent moves in percentage
 * points (40% → 45% is up 5个百分点, not 12.5%); anything else by its
 * share of the previous value. Null when there is nothing to compare: a
 * missing value (whatever its format shows for it), or a previous value
 * of zero.
 */
export function biDelta(
  current: BiCell,
  previous: BiCell,
  spec?: BiFormatSpec,
): BiDelta | null {
  const now = numeric(current);
  const before = numeric(previous);
  if (now === null || before === null) return null;
  const format = spec === undefined ? kBiNumberFormat : biDisplayFormat(spec);
  let size = 0;
  let text = '';
  if (format.kind === BiDisplayFormatKind.Percent) {
    size = Math.abs(now - before) * 100;
    text = `${digits(
      size,
      format.decimals ?? 1,
      format.decimals === undefined,
    )}个百分点`;
  } else {
    if (before === 0) return null;
    size = (Math.abs(now - before) / Math.abs(before)) * 100;
    text = `${digits(size, 1, true)}%`;
  }
  const trend = !/[1-9]/.test(text)
    ? BiDeltaTrend.Flat
    : now > before
    ? BiDeltaTrend.Up
    : BiDeltaTrend.Down;
  return { trend, text };
}

/** A move in words: `上升 12.5%`, `下降 3个百分点`, `持平`. */
export function biDeltaWords(delta: BiDelta): string {
  switch (delta.trend) {
    case BiDeltaTrend.Up:
      return `上升 ${delta.text}`;
    case BiDeltaTrend.Down:
      return `下降 ${delta.text}`;
    default:
      return '持平';
  }
}

/**
 * A stored result whose columns may carry a format shorthand (results
 * computed before full formats existed) with every format expanded.
 */
export function biResultWithDisplayFormats(
  result: BiQueryResult,
): BiQueryResult {
  const stored = (column: BiResultColumn) =>
    column.format as BiFormatSpec | undefined;
  if (!result.columns.some(column => typeof stored(column) === 'string'))
    return result;
  return {
    ...result,
    columns: result.columns.map(column => {
      const format = stored(column);
      return format === undefined
        ? column
        : { ...column, format: biDisplayFormat(format) };
    }),
  };
}
